import { Box, Button, ButtonGroup, Flex, Heading, HStack, Switch, Text } from "@chakra-ui/react";
import React from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import ReadOnlyExemptButton from "../../components/ReadOnlyExemptButton";
import { ExistingLineCapacityState } from "../../hooks/useExistingLineCapacity";
import { calcYAxisRange, generateAnnotationStarLabels } from "../../utils/chartDataCalculation";
import { formatYearMonthByLocale } from "../../utils/utils";
import { ExistingLineQuantityInput } from "./ExistingLineQuantityInput";

type Props = {
  /** useExistingLineCapacity の戻り値 */
  elq: ExistingLineCapacityState;
  /** 入力欄の年月ラベル（量産開始〜EOL） */
  labels: string[];
  /** 選択中の保存済み設定ID */
  selectedConfigIds: string[];
  /** 保存済み設定（設定名の表示用） */
  savedConfigs: { id: string; name: string }[];
  /** ★マークの基準年月（新製品＝量産開始、生産性向上＝投資開始） */
  annotationStartYearMonth: string;
  /** 投資目的（★マークの表示判定） */
  purpose?: string | null;
  /** 編集可否（false のときスイッチ・データ連携をグレーアウト。操作自体は親の fieldset disabled でも止まる） */
  isEditable?: boolean;
  /** 保存済み設定のグラフにも★マーク（開始年月の線）を表示するか（画面の既存グラフに合わせる。新製品＝false、生産性向上＝true） */
  showAnnotationOnSavedConfig?: boolean;
};

/**
 * 生産キャパグラフ（既存ライン含む）の表示ブロック
 * 見出し・要否スイッチ・入力モード切替＋グラフ（当初計画／ダウンサイド／保存済み設定）＋既存ライン生産数量の入力欄
 * 見出し・要否スイッチ・入力欄はグラフデータの計算完了を待たずに表示する（グラフ部分のみデータ有無で出し分け）
 * 新製品シミュレーション・生産性向上シミュレーション（増産のみ）で共通に使う
 */
export const ExistingLineCapacityPanel: React.FC<Props> = ({ elq, labels, selectedConfigIds, savedConfigs, annotationStartYearMonth, purpose, isEditable = true, showAnnotationOnSavedConfig = false }) => {
  const { t } = useTranslation();

  /** グラフ（当初計画／ダウンサイド／保存済み設定）を描画する。データ計算完了前は何も描画しない */
  const renderCharts = () => {
    if (!elq.chartDataOriginal) return null;

    const dataLeft = elq.chartDataOriginal;
    const dataRight = elq.chartDataModified ? elq.chartDataModified : elq.chartDataOriginal;
    const savedConfigCharts = selectedConfigIds
      .map(configId => ({
        config: savedConfigs.find(c => c.id === configId),
        chartData: elq.chartDataForSavedConfig.find(item => item.configId === configId),
      }))
      .filter(item => item.config && item.chartData?.data);

    // 積み上げ後の合計とキャパを考慮してスケールを計算
    const allData = [dataLeft, dataRight, ...savedConfigCharts.map(item => item.chartData!.data)].flatMap(ds => {
      const stackTotals: number[] = [];
      ds.datasets.filter((d: any) => d.type === 'bar').forEach((d: any) => {
        d.data.forEach((v: number, i: number) => { stackTotals[i] = (stackTotals[i] || 0) + (v || 0); });
      });
      const lineData = ds.datasets.filter((d: any) => d.type === 'line').flatMap((d: any) => d.data);
      return [...stackTotals.filter(v => v !== undefined), ...lineData];
    });
    const { yMin, yMax } = calcYAxisRange(allData);
    const commonLabels = dataLeft.labels;

    const renderChart = (key: string, title: string, chartData: any, showAnnotation: boolean) => (
      <Box key={key} textAlign="center">
        <Heading size="sm" mb={1}>
          {title}
        </Heading>
        <Box width="30vw" height="calc(30vw * 0.5625)">
          <Chart
            type="bar"
            data={{
              ...chartData,
              labels: commonLabels,
              datasets: chartData.datasets.map((ds: any) =>
                ds.type === 'line' ? { ...ds, yAxisID: 'y1' } : ds
              ),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  offset: true,
                  stacked: true,
                  ticks: { callback: (index: any) => formatYearMonthByLocale(commonLabels[index]) },
                },
                y: {
                  stacked: true,
                  min: yMin,
                  max: yMax,
                  ticks: {
                    format: {
                      notation: "standard"
                    }
                  }
                },
                y1: {
                  display: false,
                  stacked: false,
                  min: yMin,
                  max: yMax,
                },
              },
              elements: { point: { radius: 2 } },
              plugins: {
                ...(showAnnotation ? {
                  annotation: {
                    annotations: {
                      ...generateAnnotationStarLabels(annotationStartYearMonth, purpose),
                    }
                  },
                } : {}),
                datalabels: {
                  display: false,
                },
              },
            }}
          />
        </Box>
      </Box>
    );

    return (
      <Flex direction="row" justify="center" align="center" wrap="wrap" gap={4}>
        {renderChart("original", t('features.InvestmentScreening.SimulationNewProduct.heading.initialPlan'), dataLeft, true)}
        {renderChart("modified", t('features.InvestmentScreening.SimulationNewProduct.heading.downside'), dataRight, true)}
        {savedConfigCharts.map(item => renderChart(item.config!.id, item.config!.name, item.chartData!.data, showAnnotationOnSavedConfig))}
      </Flex>
    );
  };

  return (
    <Box mt={4}>
      {/* 見出しの横に要否スイッチと既存ライン生産数量の入力モード切替（シミュレーション設定とは別枠）を並べる */}
      <HStack spacing={4} mb={2}>
        <Heading size="md">
          {t('features.InvestmentScreening.SimulationNewProduct.heading.existingLineCapacity')}
        </Heading>
        <Switch
          colorScheme="blue"
          isChecked={elq.show}
          isDisabled={!isEditable}
          onChange={(e) => elq.setShow(e.target.checked)}
        >
          {t('features.InvestmentScreening.SimulationNewProduct.label.useExistingLineCapacity')}
        </Switch>
        {elq.show && (
          <>
            {/* 閲覧権限・承認完了時（fieldset disabled）でもGoogleSpreadを開けるようにする（シミュレーション設定と同じ） */}
            <ButtonGroup spacing={4} isAttached>
              <ReadOnlyExemptButton
                colorScheme={elq.inputMode === "manual" ? "green" : "gray"}
                onClick={elq.handleManualClick}
                isLoading={elq.isLoading}
              >
                {t('features.InvestmentScreening.SimulationNewProduct.button.manualInput')}
              </ReadOnlyExemptButton>
              <ReadOnlyExemptButton
                colorScheme={elq.inputMode === "google" ? "green" : "gray"}
                onClick={elq.handleGoogleSpreadClick}
                isLoading={elq.isLoading}
              >
                GoogleSpread
              </ReadOnlyExemptButton>
            </ButtonGroup>
            <Button
              colorScheme="blue"
              onClick={elq.handleSyncData}
              isDisabled={!isEditable || elq.inputMode === "manual"}
              isLoading={elq.isLoading}
            >
              {t('features.InvestmentScreening.SimulationNewProduct.button.syncData')}
            </Button>
          </>
        )}
      </HStack>
      {elq.show && (
        <Box>
          {/* 既存ライン生産数量シートの無効値エラーメッセージ */}
          {elq.syncError.invalidValueErrors.length > 0 && (
            <Box mb={2}>
              {elq.syncError.invalidValueErrors.map((error, index) => (
                <Text key={index} color="orange.500" fontSize="sm">{error}</Text>
              ))}
            </Box>
          )}
          <Text>
            unit:pcs
          </Text>
          {renderCharts()}
          {/* 既存ライン生産数量の画面入力欄（GoogleSpread入力中は編集不可） */}
          <ExistingLineQuantityInput
            labels={labels}
            data={elq.data}
            onChange={elq.setData}
            isDisabled={elq.inputMode === "google"}
          />
        </Box>
      )}
    </Box>
  );
};
